import { styled } from '@abyss/web/tools/styled';
import { Box } from '@abyss/web/ui/Box';
import { Card } from '@abyss/web/ui/Card';
import { Flex } from '@abyss/web/ui/Flex';
import { Heading } from '@abyss/web/ui/Heading';
import { Text } from '@abyss/web/ui/Text';
import Image from 'next/image';
import { ContentWidthWrapper } from '@pass-frontend/abyss/common/components/content-width-wrapper/ui';
import { MEDIA_QUERY } from '@pass-frontend/abyss/utility/style-helpers';
import { useIntl } from '@pass-frontend/common/i18n';
import { useStandaloneBreakpoints } from '@pass-frontend/features/standalone/data-bridge';

const getCustomCardCss = (borderColor: string) => ({
  'abyss-card-root': {
    width: '302px',
    border: `1px solid ${borderColor}`,
    borderRadius: '16px',
    boxShadow: 'none',
    overflow: 'hidden',
    marginTop: '0 !important',
    [MEDIA_QUERY.mobileAndTablet]: {
      width: '100%',
      height: '100%',
      marginTop: '16px !important',
    },
  },
});

const CUSTOM_CARD_SECTION_STYLES = {
  'abyss-card-section': {
    padding: '32px',
    [MEDIA_QUERY.mobileAndTablet]: {
      padding: '$lg $md',
    },
  },
};

const TEXT_CONTAINER_CSS = {
  'abyss-flex-root': {
    maxWidth: '638px',
    marginBottom: '64px',
    [MEDIA_QUERY.mobileAndTablet]: {
      maxWidth: '100%',
      marginBottom: '24px',
    },
  },
};

const DESCRIPTION_CSS = {
  'abyss-text-root': {
    fontWeight: '$normal',
    lineHeight: '1.4',
  },
};

const StyledHeading = styled(Heading, {
  marginBottom: '$lg',
  '&.abyss-heading-root': {
    fontSize: '28px',

    '& > span': {
      fontSize: '28px',
      [MEDIA_QUERY.mobileAndTablet]: {
        fontSize: '24px',
      },
    },
    [MEDIA_QUERY.mobileAndTablet]: {
      fontSize: '24px',
      marginBottom: '$md',
      lineHeight: '1.2',
    },
  },
});

const CardContainer = styled(Flex, {
  width: '100%',
  [MEDIA_QUERY.desktop]: {
    gap: '8px',
  },
  '&.abyss-flex-root': {
    flexWrap: 'nowrap',
  },
});

const CardSection = styled(Flex, {
  [MEDIA_QUERY.mobileAndTablet]: {
    '&.abyss-flex-root': {
      flexWrap: 'nowrap',
    },
  },
});

const StyledCardHeading = styled(Heading, {
  '&.abyss-heading-root': {
    marginBottom: '22px',
    fontSize: '$lg',
    [MEDIA_QUERY.mobileAndTablet]: {
      marginBottom: '$sm',
    },
  },
});

const StyledCardImage = styled(Image, {
  marginBottom: '$lg',
  width: '168px',
  [MEDIA_QUERY.mobileAndTablet]: {
    width: '56px',
  },
});

export function BenefitsOverview(props: {
  bgColor?: string;
  introText?: string;
  cardBorderColor?: string;
}) {
  const { t } = useIntl();
  const { isDesktop } = useStandaloneBreakpoints();
  const {
    bgColor = '$tint7',
    introText = t('benefitsOverview.description'),
    cardBorderColor = '$tint4',
  } = props;
  const CUSTOM_CARD_STYLES = getCustomCardCss(cardBorderColor);

  const cards = [
    {
      title: t('benefitsOverview.expansiveWorkoutTitle'),
      description: t('benefitsOverview.expansiveWorkoutDescription'),
      imageUrl: '/images/girl-doing-yoga.png',
    },
    {
      title: t('benefitsOverview.digitalWorkoutsTitle'),
      description: t('benefitsOverview.digitalWorkoutsDescription'),
      imageUrl: '/images/man-doing-squad.png',
    },
    {
      title: t('benefitsOverview.healthyPerksTitle'),
      description: t('benefitsOverview.healthyPerksDescription'),
      imageUrl: '/images/girl-holding-bag.png',
    },
    {
      title: t('benefitsOverview.familyMembershipsTitle'),
      description: t('benefitsOverview.familyMembershipsDescription'),
      imageUrl: '/images/teal-basketball-rounded.png',
    },
  ];

  return (
    <Box
      className="benefits-overview__content-width-wrapper"
      color={bgColor}
      padding={isDesktop ? '120px 0 80px 0' : '48px $sm'}
    >
      <ContentWidthWrapper>
        <Flex alignItems="center" direction="column">
          <Flex
            direction="column"
            alignItems="center"
            css={TEXT_CONTAINER_CSS}
            justify="center"
            className="benefits-overview__heading-container"
          >
            <StyledHeading offset={isDesktop ? 2 : 1} textAlign="center">
              {t('benefitsOverview.heading1')}{' '}
              <Text color="#05635C" size="$lg">
                {t('benefitsOverview.heading2')}
              </Text>
            </StyledHeading>
            <Text css={{ 'abyss-text-root': { textAlign: 'center' } }}>
              {introText}
            </Text>
          </Flex>
          <CardContainer
            direction={isDesktop ? 'row' : 'column'}
            justify="space-between"
          >
            {cards.map(({ title, description, imageUrl }, i) => (
              <Card css={CUSTOM_CARD_STYLES} size="small">
                <Card.Section css={CUSTOM_CARD_SECTION_STYLES}>
                  <CardSection
                    direction={isDesktop ? 'column' : 'row'}
                    justify="flex-start"
                    alignItems="flex-start"
                  >
                    <StyledCardImage
                      src={imageUrl}
                      width={168}
                      height={134}
                      alt={title}
                    />
                    <Flex
                      direction="column"
                      alignItems="flex-start"
                      css={{
                        'abyss-flex-root': {
                          marginLeft: isDesktop ? 0 : '$md',
                        },
                      }}
                    >
                      <StyledCardHeading offset={5}>{title}</StyledCardHeading>
                      <Text css={DESCRIPTION_CSS}>{description}</Text>
                    </Flex>
                  </CardSection>
                </Card.Section>
              </Card>
            ))}
          </CardContainer>
        </Flex>
      </ContentWidthWrapper>
    </Box>
  );
}
